import { useRef, useEffect, useCallback, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { getRealtimeBaseUrl } from "../utils/realtimeBaseUrl";
import { parseTripUpdate } from "../utils/tripTracking";

const RECONNECT_DELAYS_MS = [0, 3000, 3000, 10000, 15000];

/**
 * SignalR hub for live trip tracking (passenger / admin viewer).
 * Hub: {base}/hubs/trip?tripId={tripId}
 */
const useTripSocket = ({ onReconnected } = {}) => {
  const connectionRef = useRef(null);
  const onReconnectedRef = useRef(onReconnected);

  useEffect(() => {
    onReconnectedRef.current = onReconnected;
  }, [onReconnected]);

  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [tripUpdates, setTripUpdates] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const currentTripIdRef = useRef(null);

  const handleTripUpdate = useCallback((data) => {
    const update = parseTripUpdate(data);
    if (!update) return;

    const enriched = {
      ...update,
      receivedAt: new Date().toISOString(),
    };

    setTripUpdates((prev) => [...prev.slice(-100), enriched]);
    setLastUpdate(enriched);
  }, []);

  const createConnection = useCallback((tripId, token) => {
    const baseUrl = (getRealtimeBaseUrl() || window.location.origin).replace(
      /\/+$/,
      "",
    );
    const hubUrl = `${baseUrl}/hubs/trip?tripId=${encodeURIComponent(tripId)}`;

    return new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect(RECONNECT_DELAYS_MS)
      .configureLogging(signalR.LogLevel.Warning)
      .build();
  }, []);

  const joinTripGroup = useCallback(async (connection, tripId) => {
    await connection.invoke("JoinTripGroup", Number(tripId));
  }, []);

  const leaveTripGroup = useCallback(async (connection, tripId) => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
      return;
    }
    try {
      await connection.invoke("LeaveTripGroup", Number(tripId));
    } catch (err) {
      console.warn("LeaveTripGroup failed:", err?.message);
    }
  }, []);

  const connect = useCallback(
    async (tripId) => {
      if (!tripId || Number(tripId) <= 0) {
        setError(new Error("Invalid trip ID"));
        return false;
      }

      if (
        isConnected &&
        currentTripIdRef.current === Number(tripId) &&
        connectionRef.current?.state === signalR.HubConnectionState.Connected
      ) {
        return true;
      }

      const token = sessionStorage.getItem("ecoToken");
      if (!token) {
        setError(new Error("No authentication token"));
        return false;
      }

      if (connectionRef.current) {
        try {
          if (currentTripIdRef.current) {
            await leaveTripGroup(connectionRef.current, currentTripIdRef.current);
          }
          await connectionRef.current.stop();
        } catch {
          /* ignore */
        }
        connectionRef.current = null;
      }

      setIsConnecting(true);
      setError(null);
      currentTripIdRef.current = Number(tripId);

      try {
        const connection = createConnection(tripId, token);
        connectionRef.current = connection;

        connection.on("ReceiveTripUpdate", handleTripUpdate);

        connection.onclose(() => {
          setIsConnected(false);
        });

        connection.onreconnecting(() => {
          setIsConnected(false);
        });

        connection.onreconnected(async () => {
          setIsConnected(true);
          try {
            await joinTripGroup(connection, tripId);
          } catch (joinErr) {
            console.warn("Re-join after reconnect failed:", joinErr?.message);
          }
          onReconnectedRef.current?.();
        });

        await connection.start();
        await joinTripGroup(connection, tripId);

        setIsConnected(true);
        setIsConnecting(false);
        return true;
      } catch (err) {
        setError(err);
        setIsConnected(false);
        setIsConnecting(false);
        connectionRef.current = null;
        return false;
      }
    },
    [
      isConnected,
      createConnection,
      handleTripUpdate,
      joinTripGroup,
      leaveTripGroup,
    ],
  );

  const disconnect = useCallback(async () => {
    const connection = connectionRef.current;
    const tripId = currentTripIdRef.current;

    if (!connection) {
      setIsConnected(false);
      return;
    }

    try {
      if (tripId) {
        await leaveTripGroup(connection, tripId);
      }
      await connection.stop();
    } catch (err) {
      console.warn("SignalR disconnect:", err?.message);
    } finally {
      connectionRef.current = null;
      currentTripIdRef.current = null;
      setIsConnected(false);
    }
  }, [leaveTripGroup]);

  const getConnectionInfo = useCallback(
    () => ({
      isConnected,
      isConnecting,
      currentTripId: currentTripIdRef.current,
      connectionState: connectionRef.current?.state,
      transport: connectionRef.current?.transport?.name,
      connectionId: connectionRef.current?.connectionId,
      tripUpdatesCount: tripUpdates.length,
      lastUpdateTime: lastUpdate?.receivedAt,
      error: error?.message,
    }),
    [isConnected, isConnecting, tripUpdates.length, lastUpdate, error],
  );

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    isConnecting,
    error,
    tripUpdates,
    lastUpdate,
    currentTripId: currentTripIdRef.current,
    connect,
    disconnect,
    getConnectionInfo,
  };
};

export default useTripSocket;
